import { InspectionItem, InspectionSheet } from '@editor/interfaces/inspection'
import { PayloadAction, createSlice } from '@reduxjs/toolkit'

import { deleteInspectionItems, patchInspectionItems } from './thunks/inspectionItems'

interface InspectionItemsState {
  /**
   * Current area's inspection sheet.
   */
  inspectionSheet?: InspectionSheet

  /**
   * Mapping of inspection sheets by area ID.
   * Generally used by pages with aligned areas.
   */
  inspectionSheets: Record<string, InspectionSheet>

  /**
   * List of inspection items within the current area.
   */
  items: InspectionItem[]

  /**
   * Loading state for inspection items.
   */
  isLoading: boolean
}

const initialState: InspectionItemsState = {
  isLoading: true,
  items: [],
  inspectionSheets: {},
}

const slice = createSlice({
  name: 'inspectionItems',
  initialState,
  reducers: {
    setLoading: (state: InspectionItemsState, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setInspectionSheet: (state: InspectionItemsState, action: PayloadAction<InspectionSheet | undefined>) => {
      state.inspectionSheet = action.payload
    },
    setInspectionSheets: (state: InspectionItemsState, action: PayloadAction<Record<string, InspectionSheet>>) => {
      state.inspectionSheets = action.payload
    },
    setInspectionItems: (state: InspectionItemsState, action: PayloadAction<InspectionItem[]>) => {
      state.items = action.payload
    },
    addInspectionItems: (state: InspectionItemsState, action: PayloadAction<InspectionItem[]>) => {
      state.items.push(...action.payload)
    },
    updateInspectionItems: (state: InspectionItemsState, action: PayloadAction<Partial<InspectionItem>[]>) => {
      state.items = state.items.map((item) => {
        const updatedItem = action.payload.find((i) => i.inspection_item_id === item.inspection_item_id)
        return updatedItem ? { ...item, ...updatedItem } : item
      })
    },
    removeInspectionItems: (state: InspectionItemsState, action: PayloadAction<string[]>) => {
      state.items = state.items.filter((item) => !action.payload.includes(item.inspection_item_id!))
    },
    upsertInspectionItems: (state: InspectionItemsState, action: PayloadAction<InspectionItem[]>) => {
      action.payload.forEach((newItem) => {
        const existingItem = state.items.find((item) => item.inspection_item_id === newItem.inspection_item_id)
        if (existingItem) {
          // Update existing item
          Object.assign(existingItem, newItem)
        } else {
          // Add new item
          state.items.push(newItem)
        }
      })
    },
    reset: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(patchInspectionItems.fulfilled, (state, action) => {
        state.items = state.items.map((item) => {
          const updatedItem = action.payload?.find((i) => i.inspection_item_id === item.inspection_item_id)
          return updatedItem ? { ...item, ...updatedItem } : item
        })
      })
      .addCase(deleteInspectionItems.fulfilled, (state, action) => {
        if (!action.payload) return
        state.items = state.items.filter((item) => !action.meta.arg.items.includes(item.inspection_item_id!))
      })
  },
})

export const {
  setLoading,

  setInspectionSheet,
  setInspectionSheets,
  setInspectionItems,
  addInspectionItems,
  updateInspectionItems,
  removeInspectionItems,
  upsertInspectionItems,
  reset,
} = slice.actions
export default slice.reducer
